import { create } from "zustand";
import type { User } from "../types/user";
import type { RawBusiness } from "../types/business";

const getStoredJson = <T,>(key: string, fallback: T): T => {
  if (typeof window === "undefined") return fallback;

  try {
    const value = localStorage.getItem(key);
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
};

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  connectedBusinesses: RawBusiness[];
  loading: boolean;
  setUser: (user: User | null) => void;
  setConnectedBusinesses: (connectedBusinesses: RawBusiness[]) => void;
  logout: () => void;
}

const useAuthStore = create<AuthState>((set) => ({
  user: getStoredJson<User | null>("user", null),
  isAuthenticated: Boolean(
    typeof window !== "undefined" && localStorage.getItem("isLoggedIn") === "true",
  ),
  connectedBusinesses: (() => {
    const connectedBusinesses = getStoredJson<RawBusiness[] | null>("connected_businesses", null);
    if (Array.isArray(connectedBusinesses) && connectedBusinesses.length > 0) {
      return connectedBusinesses;
    }

    const selectedBusiness = getStoredJson<RawBusiness | null>("selected_business", null);
    return selectedBusiness ? [selectedBusiness] : [];
  })(),
  loading: false,

  setUser: (user) =>
    set({
      user,
      isAuthenticated: true,
    }),

  setConnectedBusinesses: (connectedBusinesses) =>
    set(() => {
      if (typeof window !== "undefined") {
        localStorage.setItem(
          "connected_businesses",
          JSON.stringify(connectedBusinesses),
        );
      }

      return {
        connectedBusinesses,
      };
    }),

  logout: () =>
    set(() => {
      if (typeof window !== "undefined") {
        localStorage.removeItem("connected_businesses");
      }

      return {
        user: null,
        isAuthenticated: false,
        connectedBusinesses: [],
      };
    }),
}));

export default useAuthStore;
